import { useCallback, useMemo } from 'react';
import { useGetRoleManagementUsers } from '../api/role-management-api/role-management-api';
import { TenantUserDto } from '../api/model';

// builds a readable name from a tenant user
export function userDisplayName(user?: TenantUserDto): string {
  if (!user) return '';
  const full = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return full || user.username || `#${user.id ?? ''}`;
}

export interface UseTenantUsersResult {
  users: TenantUserDto[];
  isLoading: boolean;
  byId: (id?: number | null) => TenantUserDto | undefined;
  nameById: (id?: number | null) => string;
  namesByIds: (ids?: number[] | null) => string;
}

// Loads the tenant user directory once and exposes lookup helpers
export function useTenantUsers(): UseTenantUsersResult {
  const { data, isLoading } = useGetRoleManagementUsers({
    query: { staleTime: 5 * 60 * 1000, retry: false },
  });

  const users = useMemo(() => data ?? [], [data]);

  const map = useMemo(() => {
    const m = new Map<number, TenantUserDto>();
    users.forEach(u => {
      if (u.id != null) m.set(u.id, u);
    });
    return m;
  }, [users]);

  const byId = useCallback(
    (id?: number | null) => (id == null ? undefined : map.get(id)),
    [map]
  );
  const nameById = useCallback(
    (id?: number | null) => userDisplayName(byId(id)),
    [byId]
  );
  const namesByIds = useCallback(
    (ids?: number[] | null) => {
      if (!ids || ids.length === 0) return '';
      return ids.map(id => userDisplayName(byId(id))).filter(Boolean).join(', ');
    },
    [byId]
  );

  return { users, isLoading, byId, nameById, namesByIds };
}
